import { useMutation, useQuery } from '@tanstack/react-query';
import { getUser } from 'auth';
import type { AxiosResponse } from 'axios';
import Button from 'components-stateless/DesignSystem/Atoms/Button';
import { BodyStrong } from 'components-stateless/DesignSystem/Atoms/Fonts';
import Page from 'components-stateless/DesignSystem/Atoms/Page';
import InputField from 'components-stateless/Inputs/InputField';
import RadioGroupField from 'components-stateless/Inputs/RadioGroup';
import { notify } from 'components-stateless/Notifications';
import Spinner from 'components-stateless/Spinner';
import { API_AI_PROVIDER } from 'constants/appConstants';
import axios from 'constants/axios';
import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

interface GetOpenAIProviderResponseResponse {
  aiProvider: 'openai';
  openAiApiKey: string;
}

interface GetCBOpenAIProviderResponseResponse {
  aiProvider: 'cb-openai';
}

interface GetAIProviderResponse {
  result:
    | GetOpenAIProviderResponseResponse
    | GetCBOpenAIProviderResponseResponse;
}

interface UpdateCBOpenAIProviderPayload {
  aiProvider: 'cb-openai';
}

interface UpdateOpenAIProviderPayload {
  aiProvider: 'openai';
  openAiApiKey: string;
}

type UpdateAIProviderRequest =
  | UpdateCBOpenAIProviderPayload
  | UpdateOpenAIProviderPayload;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 8px;
`;

// Styled Input component that improves asterisk alignment with default font
const MaskedInput = styled(InputField)<{ value?: string }>`
  ${({ value }) =>
    value &&
    value.includes('*') &&
    `
    input {
      letter-spacing: 0.025em;
    }
  `}
`;

export const AIProviderPage = React.memo(() => {
  const user = getUser();

  const [selectedProvider, setSelectedProvider] = useState<
    GetAIProviderResponse['result']['aiProvider'] | null
  >(null);

  const [customOpenAiApiKey, setCustomOpenAiApiKey] = useState<string>('');

  const {
    data: aiProviderResponse,
    isLoading,
    refetch,
  } = useQuery<AxiosResponse<GetAIProviderResponse>>(
    ['ai-provider', user?.accountUUID],
    {
      enabled: !!user,
      queryFn: () => axios.get(API_AI_PROVIDER),
    }
  );

  const aiProviderResult = useMemo(
    () => aiProviderResponse?.data?.result,
    [aiProviderResponse]
  );

  const { mutate: updateAIProvider, isLoading: isUpdatingAIProvider } =
    useMutation(['update-ai-provider'], {
      mutationFn: (request: UpdateAIProviderRequest) =>
        axios.patch(API_AI_PROVIDER, request),
      onSuccess: () => {
        refetch();
        notify({
          type: 'Success',
          message: 'AI Provider saved successfully.',
        });
      },
      onError: (error) => {
        notify({
          type: 'Error',
          message:
            error instanceof Error
              ? error.message
              : 'There was an error saving the AI Provider. Please try again.',
        });
      },
    });

  const effectiveProvider = useMemo(
    () => selectedProvider ?? aiProviderResult?.aiProvider,
    [aiProviderResult?.aiProvider, selectedProvider]
  );

  const effectiveOpenAiApiKey = useMemo(() => {
    if (
      effectiveProvider === 'openai' &&
      aiProviderResult?.aiProvider === 'openai' &&
      customOpenAiApiKey === ''
    ) {
      return aiProviderResult.openAiApiKey;
    }
    return customOpenAiApiKey;
  }, [aiProviderResult, customOpenAiApiKey, effectiveProvider]);

  if (!user?.isAIProviderConfigEnabled) {
    return <Navigate to="/" />;
  }

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <Page
      heading="AI Provider"
      headingDesc="Choose the AI provider used across all features."
    >
      <Container>
        <BodyStrong>API Key</BodyStrong>
        <RadioGroupField
          name="ai-provider"
          isRow
          width="fit-content"
          options={[
            {
              label: 'Checkbox Default',
              value: 'cb-openai',
            },
            {
              label: 'Custom OpenAI API Key',
              value: 'openai',
            },
          ]}
          onChange={(e) => {
            setSelectedProvider(
              e.target.value as GetAIProviderResponse['result']['aiProvider']
            );
          }}
          value={effectiveProvider}
        />
        {effectiveProvider === 'openai' && (
          <MaskedInput
            name="open-ai-api-key"
            label="API Key"
            value={effectiveOpenAiApiKey}
            onChange={(e) => {
              setCustomOpenAiApiKey(e.target.value);
            }}
          />
        )}
        <Button
          buttonSize="Small"
          buttonType="Primary"
          shape="Square"
          style={{ width: 'fit-content' }}
          loading={isUpdatingAIProvider}
          disabled={
            effectiveProvider === 'openai' &&
            (effectiveOpenAiApiKey === '' ||
              (aiProviderResult?.aiProvider === 'openai' &&
                effectiveOpenAiApiKey === aiProviderResult?.openAiApiKey))
          }
          onClick={() => {
            if (
              effectiveProvider === 'openai' &&
              effectiveOpenAiApiKey !== ''
            ) {
              updateAIProvider({
                aiProvider: effectiveProvider,
                openAiApiKey: effectiveOpenAiApiKey,
              });
            } else if (effectiveProvider === 'cb-openai') {
              updateAIProvider({
                aiProvider: effectiveProvider,
              });
            }
          }}
        >
          Save
        </Button>
      </Container>
    </Page>
  );
});

AIProviderPage.displayName = 'AIProviderPage';
